import {HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {map, switchMap, take} from 'rxjs';
import {SAP_COMMERCE_ASM_REQUEST_HEADER_NAME} from '../../features/asm/constants/headers';
import {
  ASM_CONFIG_PATH,
  BASE_SITE,
  ENDPOINT_AUTH_HEADER_EXCLUDE,
  OCC_BASE_PATH,
} from '../constants/api';
import {AsmService} from '../services/asm.service';
import {AuthService} from '../services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private auth: AuthService,
    private asmService: AsmService,
  ) {}

  private readonly EXPIRATION_BUFFER = 15;

  intercept(request: HttpRequest<any>, next: HttpHandler) {
    const url = request.url;

    const isExcludedFromAuthHeaders = ENDPOINT_AUTH_HEADER_EXCLUDE.some((entry) =>
      url.includes(entry),
    );

    if (isExcludedFromAuthHeaders) {
      return next.handle(request);
    }

    return this.auth.refreshTokenIfExpired(this.EXPIRATION_BUFFER).pipe(
      switchMap(() => {
        const authorizedRequest = this.addAuthHeaders(request);

        const isAsmConfigRequest = url.includes(OCC_BASE_PATH + BASE_SITE + ASM_CONFIG_PATH);
        // Skip ASM impersonation logic for the ASM config endpoint
        if (isAsmConfigRequest) {
          return next.handle(authorizedRequest);
        }

        // Add ASM impersonation header if available
        return this.addAsmHeaders(authorizedRequest).pipe(
          switchMap((requestWithAsm) => next.handle(requestWithAsm)),
        );
      }),
    );
  }

  private addAuthHeaders(request: HttpRequest<any>) {
    const accessToken = this.auth.getAccessToken();
    const idToken = this.auth.getIdToken();

    if (!accessToken || !idToken) {
      return request;
    }

    const isCommsApi = request.url.includes('/api/comms/');

    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
    };

    if (!isCommsApi) {
      headers['Idtoken'] = idToken;
    }

    // cx-srv-* services behind the shell authorize on X-ID-Token (403 without it).
    const isCxServiceApi =
      request.url.startsWith('/api/user') || request.url.startsWith('/api/deals') || isCommsApi;

    if (isCxServiceApi) {
      headers['X-ID-Token'] = idToken;
    }

    return request.clone({setHeaders: headers});
  }

  private addAsmHeaders(request: HttpRequest<any>) {
    return this.asmService.getImpersonateUser().pipe(
      take(1),
      map((impersonateUser) => {
        if (impersonateUser?.email) {
          return request.clone({
            setHeaders: {
              [SAP_COMMERCE_ASM_REQUEST_HEADER_NAME]: impersonateUser.email,
            },
          });
        }
        return request;
      }),
    );
  }
}
